/**
 * RubricSection - Subjective Grading Configuration for V2
 *
 * Features:
 * - Add/Edit/Delete rubric criteria and ratings
 * - Drag-drop reordering
 * - CSV upload
 * - AI suggestions (on initial "+" Rubric click)
 * - Cancel/Submit pattern (like V1)
 */
import log from 'loglevel'
import React, { useEffect, useState, useRef } from 'react'
import { Button, Spinner, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FiPlus, FiUpload, FiDownload, FiTrash2, FiCheck, FiCopy } from 'react-icons/fi'
import { MdClose, MdDragIndicator } from 'react-icons/md'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import * as constants from 'Constants'
import httpService from 'services/httpService'
import displayGlobalMessage from 'Utils/DisplayGlobalMessage'
import UploadRubricModal from './UploadRubricModal'
import ClearRubricModal from './ClearRubricModal'
import ConfirmationModal from 'Components/Common/ConfirmationModal'
import styles from '../ProgrammingActivityV2.module.css'

const RubricSection = ({ activityId, questionId, isQuiz = false }) => {
  const targetId = questionId || activityId
  const isQuizMode = Boolean(isQuiz || questionId)

  // Rubric data state
  const [rubricData, setRubricData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Deleted IDs for API update
  const [deletedCriteriaIds, setDeletedCriteriaIds] = useState([])
  const [deletedRatingIds, setDeletedRatingIds] = useState([])

  // Editing state
  const [editingCell, setEditingCell] = useState(null) // { criteriaIndex, ratingIndex (null for criteria) }

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showClearModal, setShowClearModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState({ show: false, criteriaIndex: null, ratingIndex: null, name: '' })
  const [hasGradedSubmissions, setHasGradedSubmissions] = useState(false)

  // Refs for inline editing
  const nameRef = useRef(null)
  const descriptionRef = useRef(null)
  const marksRef = useRef(null)

  // Default structures (matching V1)
  const defaultCriteria = {
    criteria_id: -1,
    name: 'Untitled criteria',
    description: '',
    isModelAvail: false,
    Ratings: [
      { rating_id: -1, tag: 'Untitled Rating 1', description: '', mark: 10 },
      { rating_id: -1, tag: 'Untitled Rating 2', description: '', mark: 7 }
    ]
  }

  const defaultRating = {
    rating_id: -1,
    tag: 'Untitled Rating',
    description: '',
    mark: 0
  }

  // Fetch rubric on mount
  useEffect(() => {
    if (targetId) {
      fetchRubric()
    }
  }, [targetId])

  // Fetch rubric from API
  const fetchRubric = async () => {
    if (!targetId) return

    setIsLoading(true)
    try {
      const url = isQuizMode
        ? `${constants.base_url}/otherlabs/question/${targetId}/rubrics/`
        : `${constants.base_url}/evalpro/rubric/read_rubric/${targetId}?include_stats=true`
      const response = await httpService.get(url)

      const rawList = response.data?.rubric ? response.data.rubric : response.data
      setHasGradedSubmissions(Boolean(response.data?.has_evaluations))
      const data = (rawList || []).map((criteria) => ({
        criteria_id: criteria.id || criteria.criteria_id,
        name: criteria.title || criteria.name,
        description: criteria.description || '',
        isModelAvail: criteria.is_ai_grading_available || false,
        Ratings: (criteria.Ratings || criteria.ratings || []).map((rating) => ({
          rating_id: rating.id || rating.rating_id,
          tag: rating.title || rating.tag,
          description: rating.description || '',
          mark: rating.marks !== undefined ? rating.marks : rating.mark
        })).sort((a, b) => b.mark - a.mark)
      }))

      setRubricData(data)
      setDeletedCriteriaIds([])
      setDeletedRatingIds([])
    } catch (error) {
      log.error('Error fetching rubric:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Save rubric to API
  const saveRubric = async (data = rubricData) => {
    if (!targetId) return

    // 1. Validate Criteria Names: non-empty & unique
    const criteriaNames = data.map((c) => (c.name || '').trim())
    for (let i = 0; i < criteriaNames.length; i++) {
      if (!criteriaNames[i]) {
        displayGlobalMessage(`Criteria name cannot be empty (Row #${i + 1})`, 'error')
        return
      }
    }
    const lowerCritNames = criteriaNames.map(n => n.toLowerCase())
    for (let i = 0; i < lowerCritNames.length; i++) {
      if (lowerCritNames.indexOf(lowerCritNames[i]) !== i) {
        displayGlobalMessage(`Duplicate criteria name "${criteriaNames[i]}". Each criteria must have a unique title.`, 'error')
        return
      }
    }

    // 2. Validate Ratings per criterion: non-empty titles, unique titles, unique non-negative marks
    for (let i = 0; i < data.length; i++) {
      const crit = data[i]
      const ratings = crit.Ratings || []
      if (ratings.length === 0) {
        displayGlobalMessage(`Criteria "${crit.name}" must have at least one rating.`, 'error')
        return
      }

      const ratingTags = ratings.map(r => (r.tag || '').trim())
      for (let j = 0; j < ratingTags.length; j++) {
        if (!ratingTags[j]) {
          displayGlobalMessage(`Rating name cannot be empty in criteria "${crit.name}"`, 'error')
          return
        }
      }

      const lowerTags = ratingTags.map(t => t.toLowerCase())
      for (let j = 0; j < lowerTags.length; j++) {
        if (lowerTags.indexOf(lowerTags[j]) !== j) {
          displayGlobalMessage(`Duplicate rating name "${ratingTags[j]}" in criteria "${crit.name}". Ratings under a criteria must have unique titles.`, 'error')
          return
        }
      }

      const marks = ratings.map(r => parseFloat(r.mark))
      for (let j = 0; j < marks.length; j++) {
        if (isNaN(marks[j])) {
          displayGlobalMessage(`Please enter a valid numeric mark for "${ratingTags[j]}" in criteria "${crit.name}"`, 'error')
          return
        }
        if (marks[j] < 0) {
          displayGlobalMessage(`Marks cannot be negative for "${ratingTags[j]}" in criteria "${crit.name}"`, 'error')
          return
        }
      }
    }

    // 3. Construct dual-compatible payload
    const payload = data.map((crit, idx) => ({
      criteria_id: crit.criteria_id,
      id: crit.criteria_id,
      name: (crit.name || '').trim(),
      title: (crit.name || '').trim(),
      description: crit.description || '',
      max_marks: getMaxMarks(crit.Ratings),
      order: idx + 1,
      isModelAvail: crit.isModelAvail || false,
      Ratings: (crit.Ratings || []).map((rating, rIdx) => ({
        rating_id: rating.rating_id,
        id: rating.rating_id,
        tag: (rating.tag || '').trim(),
        title: (rating.tag || '').trim(),
        description: rating.description || '',
        mark: parseFloat(rating.mark) || 0,
        marks: parseFloat(rating.mark) || 0,
        order: rIdx + 1
      }))
    }))

    setIsSaving(true)
    try {
      const url = isQuizMode
        ? `${constants.base_url}/otherlabs/question/${targetId}/rubrics/`
        : `${constants.base_url}/evalpro/rubric/update_rubric/${targetId}`
      await httpService.post(url, {
        rubric: payload,
        deleted_ids: [deletedCriteriaIds, deletedRatingIds]
      })

      // Refresh to get updated IDs
      await fetchRubric()
      displayGlobalMessage('Rubric saved successfully', 'success')
    } catch (error) {
      log.error('Error saving rubric:', error)
      const errorMsg = error?.response?.data?.error || 'Error saving rubric'
      displayGlobalMessage(errorMsg, 'error')
    } finally {
      setIsSaving(false)
    }
  }

  // Add new rubric (with suggestions or default) - like V1, only sets local state
  const handleAddRubric = async () => {
    if (!targetId) {
      displayGlobalMessage(
        isQuizMode ? 'Please save the question first' : 'Please save the activity first',
        'warning'
      )
      return
    }

    if (isQuizMode) {
      setRubricData([{ ...defaultCriteria }])
      return
    }

    // Try to get AI suggestions first
    setIsLoading(true)
    try {
      const url = `${constants.base_url}/evalpro/rubric/suggest_rubric/${targetId}`
      const response = await httpService.get(url)

      const suggestions = Object.entries(response.data).map(([criterion, ratings]) => ({
        criteria_id: -1,
        name: criterion,
        description: '',
        isModelAvail: true,
        Ratings: ratings.map((value) => ({
          rating_id: -1,
          tag: value,
          description: '',
          mark: 0
        }))
      }))

      if (suggestions.length > 0) {
        setRubricData(suggestions)
      } else {
        // No suggestions, add default criteria
        setRubricData([{ ...defaultCriteria }])
      }
    } catch (error) {
      // On error, add default criteria
      setRubricData([{ ...defaultCriteria }])
    } finally {
      setIsLoading(false)
    }
  }

  // Add criteria (local state only - no API call)
  const handleAddCriteria = () => {
    const existingNames = new Set(rubricData.map(c => (c.name || '').toLowerCase()))
    let baseName = 'Untitled Criteria'
    let name = baseName
    let counter = 1
    while (existingNames.has(name.toLowerCase())) {
      counter++
      name = `${baseName} ${counter}`
    }
    setRubricData([...rubricData, {
      ...defaultCriteria,
      name,
      Ratings: [{ ...defaultRating }]
    }])
  }

  // Helper to generate unique name for cloned criteria
  const getUniqueCloneName = (baseName, existingData) => {
    let newName = `${baseName} (Copy)`
    let counter = 2
    const existingNames = new Set(existingData.map((c) => (c.name || '').toLowerCase()))
    while (existingNames.has(newName.toLowerCase())) {
      newName = `${baseName} (Copy ${counter})`
      counter++
    }
    return newName
  }

  // Clone criteria with all its ratings into the next row
  const handleCloneCriteria = (index) => {
    const sourceCriteria = rubricData[index]
    const clonedCriteria = {
      criteria_id: -1,
      name: getUniqueCloneName(sourceCriteria.name, rubricData),
      description: sourceCriteria.description || '',
      isModelAvail: false,
      Ratings: (sourceCriteria.Ratings || []).map((rating) => ({
        rating_id: -1,
        tag: rating.tag,
        description: rating.description || '',
        mark: rating.mark
      }))
    }
    const newData = [...rubricData]
    newData.splice(index + 1, 0, clonedCriteria)
    setRubricData(newData)
    setEditingCell(null)
  }

  // Delete criteria (local state only - no API call)
  const handleDeleteCriteria = (index) => {
    const criteria = rubricData[index]
    if (criteria.criteria_id !== -1) {
      setDeletedCriteriaIds([...deletedCriteriaIds, criteria.criteria_id])
    }
    setRubricData(rubricData.filter((_, i) => i !== index))
    setEditingCell(null)
  }

  // Add rating to criteria (local state only - no API call)
  const handleAddRating = (criteriaIndex) => {
    const newData = [...rubricData]
    const ratings = [...newData[criteriaIndex].Ratings]
    const existingTags = new Set(ratings.map((r) => (r.tag || '').toLowerCase()))
    let tag = 'Untitled Rating'
    let counter = 1
    while (existingTags.has(tag.toLowerCase())) {
      counter++
      tag = `Untitled Rating ${counter}`
    }

    const existingMarks = new Set(ratings.map((r) => parseFloat(r.mark)))
    let mark = 0
    while (existingMarks.has(mark)) {
      mark = Math.round((mark + 1) * 10) / 10
    }

    newData[criteriaIndex].Ratings = [
      ...ratings,
      {
        rating_id: -1,
        tag,
        description: '',
        mark
      }
    ]
    newData[criteriaIndex].isModelAvail = false
    setRubricData(newData)
  }

  // Insert rating into criteria at a specific index
  const handleInsertRating = (criteriaIndex, insertIndex) => {
    const newData = [...rubricData]
    const ratings = [...newData[criteriaIndex].Ratings]

    // Calculate reasonable default marks if inserted between two ratings
    let defaultMark = 0
    if (insertIndex > 0 && insertIndex < ratings.length) {
      const prevMark = parseFloat(ratings[insertIndex - 1].mark) || 0
      const nextMark = parseFloat(ratings[insertIndex].mark) || 0
      defaultMark = Math.round(((prevMark + nextMark) / 2) * 10) / 10
    } else if (insertIndex > 0) {
      defaultMark = Math.max(0, (parseFloat(ratings[insertIndex - 1].mark) || 0) - 1)
    }

    // Ensure mark is unique across ratings in this criterion
    const existingMarks = new Set(ratings.map((r) => parseFloat(r.mark)))
    if (existingMarks.has(defaultMark)) {
      let testMark = defaultMark
      while (existingMarks.has(testMark)) {
        testMark = Math.round((testMark + 0.5) * 10) / 10
      }
      defaultMark = testMark
    }

    // Generate unique rating tag
    const existingTags = new Set(ratings.map((r) => (r.tag || '').toLowerCase()))
    let tag = 'Untitled Rating'
    let counter = 1
    while (existingTags.has(tag.toLowerCase())) {
      counter++
      tag = `Untitled Rating ${counter}`
    }

    const newRating = {
      rating_id: -1,
      tag: tag,
      description: '',
      mark: defaultMark
    }

    ratings.splice(insertIndex, 0, newRating)
    newData[criteriaIndex].Ratings = ratings
    newData[criteriaIndex].isModelAvail = false
    setRubricData(newData)
    setEditingCell({ criteriaIndex, ratingIndex: insertIndex })
  }

  // Delete rating (local state only - no API call)
  const handleDeleteRating = (criteriaIndex, ratingIndex) => {
    const rating = rubricData[criteriaIndex].Ratings[ratingIndex]
    if (rating.rating_id !== -1) {
      setDeletedRatingIds([...deletedRatingIds, rating.rating_id])
    }

    const newData = [...rubricData]
    newData[criteriaIndex].isModelAvail = false

    if (newData[criteriaIndex].Ratings.length > 1) {
      newData[criteriaIndex].Ratings = newData[criteriaIndex].Ratings.filter((_, i) => i !== ratingIndex)
    } else {
      newData[criteriaIndex].Ratings = [{ ...defaultRating }]
    }

    setRubricData(newData)
    setEditingCell(null)
  }

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, criteriaIndex: null, ratingIndex: null, name: '' })
  }

  const confirmDelete = () => {
    if (deleteModal.ratingIndex === null) {
      handleDeleteCriteria(deleteModal.criteriaIndex)
    } else {
      handleDeleteRating(deleteModal.criteriaIndex, deleteModal.ratingIndex)
    }
    closeDeleteModal()
  }

  // Handle criteria edit (local state only - save on Ctrl+Enter or Enter in input)
  const handleCriteriaEdit = (e, criteriaIndex) => {
    // Save on Ctrl+Enter (works in both input and textarea) or Enter in input (not textarea)
    const shouldSave = (e.key === 'Enter' && e.ctrlKey) ||
                       (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA')

    if (shouldSave) {
      e.preventDefault()
      const newData = [...rubricData]

      if (nameRef.current?.value !== newData[criteriaIndex].name) {
        newData[criteriaIndex].isModelAvail = false
      }

      newData[criteriaIndex].name = nameRef.current?.value || newData[criteriaIndex].name
      newData[criteriaIndex].description = descriptionRef.current?.value || ''

      setRubricData(newData)
      setEditingCell(null)
    }
  }

  // Handle rating edit (local state only - save on Ctrl+Enter or Enter in input)
  const handleRatingEdit = (e, criteriaIndex, ratingIndex) => {
    // Save on Ctrl+Enter (works in both input and textarea) or Enter in input (not textarea)
    const shouldSave = (e.key === 'Enter' && e.ctrlKey) ||
                       (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA')

    if (shouldSave) {
      e.preventDefault()
      const newData = [...rubricData]
      const oldTag = newData[criteriaIndex].Ratings[ratingIndex].tag

      if (nameRef.current?.value !== oldTag) {
        newData[criteriaIndex].isModelAvail = false
      }

      newData[criteriaIndex].Ratings[ratingIndex] = {
        ...newData[criteriaIndex].Ratings[ratingIndex],
        tag: nameRef.current?.value || oldTag,
        description: descriptionRef.current?.value || '',
        mark: parseFloat(marksRef.current?.value) || 0
      }

      setRubricData(newData)
      setEditingCell(null)
    }
  }

  // Handle drag end (local state only - no API call)
  const handleDragEnd = (result) => {
    if (!result.destination) return

    const newData = [...rubricData]
    const [moved] = newData.splice(result.source.index, 1)
    newData.splice(result.destination.index, 0, moved)

    setRubricData(newData)
  }

  // Clear rubric (like V1 - call API then fetch)
  const handleClearRubric = async () => {
    if (!targetId) return

    try {
      if (isQuizMode) {
        const url = `${constants.base_url}/otherlabs/question/${targetId}/rubrics/`
        await httpService.delete(url)
      } else {
        const url = `${constants.base_url}/evalpro/rubric/clear_rubric/${targetId}`
        await httpService.post(url, { rubric: rubricData })
      }

      // Fetch from database like V1 does
      await fetchRubric()
      displayGlobalMessage('Rubric cleared successfully', 'success')
    } catch (error) {
      log.error('Error clearing rubric:', error)
      displayGlobalMessage('Error clearing rubric', 'error')
    } finally {
      setShowClearModal(false)
    }
  }

  // Cancel - reset to database state (like V1)
  const handleCancel = () => {
    fetchRubric()
  }

  // Submit - save to database (like V1)
  const handleSubmit = async () => {
    await saveRubric()
  }

  // Upload success handler
  const handleUploadSuccess = () => {
    setShowUploadModal(false)
    fetchRubric()
  }

  // Download rubric as standard Bodhitree CSV matrix
  const handleDownloadRubric = () => {
    if (!rubricData || rubricData.length === 0) {
      displayGlobalMessage('No rubric to download', 'warning')
      return
    }

    try {
      // Determine max number of ratings across criteria
      let maxRatings = 0
      rubricData.forEach((crit) => {
        const len = (crit.Ratings || []).length
        if (len > maxRatings) maxRatings = len
      })

      // Rating column headers: A, B, C, ...
      const ratingCols = []
      for (let i = 0; i < maxRatings; i++) {
        ratingCols.push(String.fromCharCode(65 + i))
      }

      // Rows matching standard Bodhitree format
      const rows = [
        ['Criteria', ...ratingCols],
        ['Ratings', ...ratingCols]
      ]

      rubricData.forEach((crit) => {
        const title = (crit.name || crit.title || '').trim()
        const desc = (crit.description || '').trim()
        let critText = title
        if (title && desc && title !== desc) {
          critText = `${title}: ${desc}`
        } else if (desc) {
          critText = desc
        }
        const maxMarks = getMaxMarks(crit.Ratings)
        const critCell = `${critText}\nMarks: ${maxMarks}`

        const row = [critCell]
        const ratings = crit.Ratings || []
        for (let i = 0; i < maxRatings; i++) {
          if (i < ratings.length) {
            const r = ratings[i]
            const rDesc = (r.description || '').trim() || (r.tag || r.title || '').trim()
            const mark = r.mark !== undefined ? r.mark : (r.marks !== undefined ? r.marks : 0)
            row.push(`${rDesc}\nMarks: ${mark}`)
          } else {
            row.push('')
          }
        }
        rows.push(row)
      })

      // CSV encoding with proper RFC 4180 escaping
      const csvContent = rows
        .map((row) =>
          row
            .map((cell) => {
              const str = String(cell ?? '')
              if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
                return `"${str.replace(/"/g, '""')}"`
              }
              return str
            })
            .join(',')
        )
        .join('\r\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const filename = isQuizMode
        ? `question_${targetId}_rubric.csv`
        : `programming_activity_${targetId}_rubric.csv`
      link.setAttribute('href', downloadUrl)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(downloadUrl)

      displayGlobalMessage('Rubric downloaded successfully', 'success')
    } catch (err) {
      log.error('Error generating rubric CSV:', err)
      displayGlobalMessage('Failed to download rubric CSV', 'error')
    }
  }

  // Get max marks for a criteria
  const getMaxMarks = (ratings) => {
    if (!ratings || ratings.length === 0) return 0
    return Math.max(...ratings.map(r => parseFloat(r.mark) || 0))
  }

  // Render empty state
  if (!targetId) {
    return (
      <div className={styles.formCard}>
        <h3 className={styles.sectionTitle}>Subjective Grading Configuration</h3>
        <p className={styles.emptyStateText}>
          {isQuizMode ? 'Save the question first to configure rubrics.' : 'Save the activity first to configure rubrics.'}
        </p>
      </div>
    )
  }

  if (isLoading && rubricData.length === 0) {
    return (
      <div className={styles.formCard}>
        <h3 className={styles.sectionTitle}>Subjective Grading Configuration</h3>
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      </div>
    )
  }

  // Render empty rubric state
  if (rubricData.length === 0) {
    return (
      <div className={styles.formCard}>
        <h3 className={styles.sectionTitle}>Subjective Grading Configuration</h3>

        <div className={styles.rubricActions}>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddRubric}
            disabled={isLoading}
            className="d-flex align-items-center gap-2"
          >
            <FiPlus size={16} />
            Rubric
          </Button>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowUploadModal(true)}
            className="d-flex align-items-center gap-2"
          >
            <FiUpload size={16} />
            Upload Rubrics
          </Button>
        </div>

        <UploadRubricModal
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          activityId={activityId}
          questionId={questionId}
          isQuiz={isQuizMode}
          onSuccess={handleUploadSuccess}
        />
      </div>
    )
  }

  // Render rubric table
  return (
    <div className={styles.formCard}>
      <h3 className={styles.sectionTitle}>Subjective Grading Configuration</h3>

      {/* AI availability message */}
      {rubricData.some(c => c.isModelAvail) && (
        <div className={styles.aiMessage}>
          <FiCheck className="text-success" />
          <span>AI-powered grading is available for criteria marked with <span className="text-success">green</span>.</span>
        </div>
      )}

      {/* Rubric table */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Table bordered responsive className={styles.rubricTable}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '250px' }}>Criteria</th>
              <th>Ratings</th>
            </tr>
          </thead>
          <Droppable droppableId="rubric-table">
            {(provided) => (
              <tbody ref={provided.innerRef} {...provided.droppableProps}>
                {rubricData.map((criteria, criteriaIndex) => (
                  <Draggable
                    key={`${criteriaIndex}-${criteria.name}`}
                    draggableId={`${criteriaIndex}-${criteria.name}`}
                    index={criteriaIndex}
                  >
                    {(provided, snapshot) => (
                      <tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={snapshot.isDragging ? styles.dragging : ''}
                      >
                        {/* Drag handle, clone & delete */}
                        <td className={styles.rubricActionCell}>
                          <div {...provided.dragHandleProps} className={styles.dragHandle} title="Drag to reorder">
                            <MdDragIndicator size={18} />
                          </div>
                          <button
                            type="button"
                            className={styles.cloneBtn}
                            onClick={() => handleCloneCriteria(criteriaIndex)}
                            title="Clone criteria in next row"
                          >
                            <FiCopy size={15} />
                          </button>
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => setDeleteModal({ show: true, criteriaIndex, ratingIndex: null, name: criteria.name })}
                            title="Delete criteria"
                          >
                            <MdClose size={16} />
                          </button>
                        </td>

                        {/* Criteria cell */}
                        <td
                          className={styles.rubricCriteriaCell}
                          onClick={() => setEditingCell({ criteriaIndex, ratingIndex: null })}
                        >
                          {editingCell?.criteriaIndex === criteriaIndex && editingCell?.ratingIndex === null ? (
                            <div className={styles.rubricEditForm}>
                              <input
                                type="text"
                                ref={nameRef}
                                defaultValue={criteria.name}
                                placeholder="Criteria name"
                                className={styles.rubricInput}
                                onKeyDown={(e) => handleCriteriaEdit(e, criteriaIndex)}
                                autoFocus
                              />
                              <textarea
                                ref={descriptionRef}
                                defaultValue={criteria.description}
                                placeholder="Description (optional)"
                                className={styles.rubricTextarea}
                                rows={2}
                                onKeyDown={(e) => handleCriteriaEdit(e, criteriaIndex)}
                              />
                              <small className="text-muted">Ctrl+Enter to save</small>
                            </div>
                          ) : (
                            <div>
                              <div className={criteria.isModelAvail ? 'text-success fw-bold' : 'fw-bold'}>
                                {criteria.isModelAvail && (
                                  <OverlayTrigger overlay={<Tooltip>AI grading available</Tooltip>}>
                                    <FiCheck className="me-1" />
                                  </OverlayTrigger>
                                )}
                                {criteria.name}
                              </div>
                              <small className="text-muted">
                                {criteria.description || 'Click to add description'}
                              </small>
                              <div className="mt-1">
                                <small><strong>Max: {getMaxMarks(criteria.Ratings)}</strong></small>
                              </div>
                            </div>
                          )}
                        </td>

                        {/* Ratings cell */}
                        <td className={styles.rubricRatingsCell}>
                          <div className={styles.ratingsContainer}>
                            {criteria.Ratings.map((rating, ratingIndex) => (
                              <React.Fragment key={ratingIndex}>
                                <div
                                  className={styles.ratingItem}
                                  onClick={() => setEditingCell({ criteriaIndex, ratingIndex })}
                                >
                                  <button
                                    type="button"
                                    className={styles.ratingDeleteBtn}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setDeleteModal({ show: true, criteriaIndex, ratingIndex, name: rating.tag })
                                    }}
                                  >
                                    <FiTrash2 size={12} />
                                  </button>

                                  {editingCell?.criteriaIndex === criteriaIndex && editingCell?.ratingIndex === ratingIndex ? (
                                    <div className={styles.rubricEditForm}>
                                      <input
                                        type="text"
                                        ref={nameRef}
                                        defaultValue={rating.tag}
                                        placeholder="Rating name"
                                        className={styles.rubricInput}
                                        onKeyDown={(e) => handleRatingEdit(e, criteriaIndex, ratingIndex)}
                                        autoFocus
                                      />
                                      <textarea
                                        ref={descriptionRef}
                                        defaultValue={rating.description}
                                        placeholder="Description"
                                        className={styles.rubricTextarea}
                                        rows={2}
                                        onKeyDown={(e) => handleRatingEdit(e, criteriaIndex, ratingIndex)}
                                      />
                                      <input
                                        type="number"
                                        ref={marksRef}
                                        defaultValue={rating.mark}
                                        onKeyDown={(e) => handleRatingEdit(e, criteriaIndex, ratingIndex)}
                                        placeholder="Marks"
                                        className={styles.rubricInput}
                                        step="0.5"
                                      />
                                    </div>
                                  ) : (
                                    <div>
                                      <div className="fw-bold">{rating.tag}</div>
                                      <small className="text-muted">
                                        {rating.description || 'No description'}
                                      </small>
                                      <div><small><strong>Marks: {rating.mark}</strong></small></div>
                                    </div>
                                  )}
                                </div>

                                {ratingIndex < criteria.Ratings.length - 1 && (
                                  <div className={styles.ratingInsertDivider}>
                                    <button
                                      type="button"
                                      className={styles.ratingInsertBtn}
                                      onClick={() => handleInsertRating(criteriaIndex, ratingIndex + 1)}
                                      title="Insert rating here"
                                    >
                                      <FiPlus size={13} />
                                    </button>
                                  </div>
                                )}
                              </React.Fragment>
                            ))}

                            {/* Add rating button */}
                            <button
                              type="button"
                              className={styles.addRatingBtn}
                              onClick={() => handleAddRating(criteriaIndex)}
                            >
                              <FiPlus size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>

      {/* Action buttons - matching V1 layout: + ADD, Cancel, Submit, Upload Rubrics, Clear Rubrics */}
      <div className={styles.rubricActions}>
        <Button
          variant="primary"
          size="sm"
          onClick={handleAddCriteria}
        >
          + ADD
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={handleCancel}
          className="ms-2"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={isSaving}
          className="ms-2"
        >
          {isSaving ? 'Saving...' : 'Submit'}
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowUploadModal(true)}
          className="ms-2"
        >
          Upload Rubrics
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={handleDownloadRubric}
          className="ms-2"
        >
          Download Rubrics
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowClearModal(true)}
          className="ms-2"
        >
          Clear Rubrics
        </Button>
      </div>

      {/* Modals */}
      <UploadRubricModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        activityId={activityId}
        questionId={questionId}
        isQuiz={isQuizMode}
        onSuccess={handleUploadSuccess}
      />

      <ClearRubricModal
        show={showClearModal}
        onHide={() => setShowClearModal(false)}
        onConfirm={handleClearRubric}
      />

      <ConfirmationModal
        show={deleteModal.show}
        title={deleteModal.ratingIndex === null ? 'Delete Criteria?' : 'Delete Rating?'}
        message={`Are you sure you want to delete this ${deleteModal.ratingIndex === null ? 'criteria and all its ratings' : 'rating'}?`}
        itemName={deleteModal.name}
        warningText={
          hasGradedSubmissions
            ? 'Warning: Submissions have already been evaluated with this rubric. Deleting this will alter or invalidate existing student grades.'
            : 'This action will take effect when you submit the rubric.'
        }
        onConfirm={confirmDelete}
        onCancel={closeDeleteModal}
      />
    </div>
  )
}

export default RubricSection
